import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileText, Trash2 } from "lucide-react";
import { useAttachmentPreview } from "@/components/task/useAttachmentPreview";
import FilePreviewSheet, { canPreview } from "@/components/files/FilePreviewSheet";
import { chunkedUpload } from "@/lib/chunkedUpload";

const AttachmentThumb = ({ att, isImg, isPdf, thumb, pdfReady, canvasRef, onDownload }) => {
  if (isImg) {
    return thumb ? (
      <button onClick={onDownload} className="shrink-0" title="Download" data-testid={`attachment-thumb-${att.file_id}`}>
        <img src={thumb} alt={att.original_filename} className="h-11 w-11 object-cover rounded-sm border border-zinc-200 hover:opacity-80 transition-opacity" />
      </button>
    ) : (
      <div className="h-11 w-11 rounded-sm bg-zinc-100 animate-pulse shrink-0" />
    );
  }
  if (isPdf) {
    return (
      <button onClick={onDownload} className="shrink-0 relative" title="Download" data-testid={`attachment-pdf-${att.file_id}`}>
        <canvas ref={canvasRef} className={`w-11 rounded-sm border border-zinc-200 bg-white hover:opacity-80 transition-opacity ${pdfReady ? "" : "hidden"}`} style={{ maxHeight: 60 }} />
        {!pdfReady && <div className="h-11 w-11 rounded-sm bg-zinc-100 flex items-center justify-center"><FileText className="h-4 w-4 text-zinc-500" /></div>}
      </button>
    );
  }
  return <FileText className="h-4 w-4 text-zinc-500 shrink-0" />;
};

export default function AttachmentRow({ att, canManage, onDelete, taskId, onMarkupSaved }) {
  const isImg = (att.content_type || "").startsWith("image/");
  const isPdf = att.content_type === "application/pdf" || (att.original_filename || "").toLowerCase().endsWith(".pdf");
  const { rowRef, canvasRef, blobRef, thumb, pdfReady } = useAttachmentPreview(att, isImg, isPdf);
  const [preview, setPreview] = useState(null);

  const download = async () => {
    try {
      const data = blobRef.current || (await api.get(`/files/${att.storage_path}`, { responseType: "blob" })).data;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download file"); }
  };

  const openOrDownload = () => (canPreview(att) ? setPreview(att) : download());

  const saveMarkup = taskId ? async (markedFile) => {
    const newAtt = await chunkedUpload({ base: `/tasks/${taskId}/attachments`, file: markedFile });
    onMarkupSaved?.(newAtt);
    toast.success("Marked-up version saved");
  } : undefined;

  return (
    <div ref={rowRef} className="flex items-center gap-3 border border-zinc-200 rounded-sm px-3 py-2" data-testid={`attachment-${att.file_id}`}>
      <AttachmentThumb att={att} isImg={isImg} isPdf={isPdf} thumb={thumb} pdfReady={pdfReady} canvasRef={canvasRef} onDownload={openOrDownload} />
      <button onClick={openOrDownload} className="flex-1 text-left text-sm truncate hover:underline" data-testid={`attachment-name-${att.file_id}`}>{att.original_filename}</button>
      <span className="text-[11px] text-zinc-400 shrink-0">{(att.size / 1024).toFixed(0)} KB</span>
      {canManage && (
        <button onClick={() => onDelete(att.file_id)} className="text-zinc-400 hover:text-red-600 transition-colors shrink-0">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <FilePreviewSheet file={preview} onClose={() => setPreview(null)} onSaveMarkup={saveMarkup} />
    </div>
  );
}
